import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, AlertCircle, Wallet, ArrowLeft } from 'lucide-react';
import useWalletStore from '../../store/walletStore';

const FundingCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('');
  const [transaction, setTransaction] = useState(null);
  const { verifyFunding } = useWalletStore();

  useEffect(() => {
    // Monnify uses paymentReference
    const reference = searchParams.get('paymentReference') || 
                      searchParams.get('reference') || 
                      searchParams.get('transactionReference');

    console.log('Callback params:', Object.fromEntries(searchParams));
    console.log('Reference found:', reference);

    const verifyTransaction = async () => {
      try {
        if (!reference) {
          // Don't show error immediately - maybe webhook already processed it
          setStatus('verifying');
          setTimeout(async () => {
            // Try to check if wallet was already credited
            const wallet = await useWalletStore.getState().getWallet();
            if (wallet) {
              setStatus('success');
              setMessage('Payment completed successfully!');
            } else {
              setStatus('error');
              setMessage('No transaction reference found');
            }
          }, 3000);
          return;
        }

        const result = await verifyFunding(reference);
        
        if (result.success) {
          setStatus('success');
          setMessage('Payment verified successfully!');
          setTransaction(result.data?.transaction);
        } else {
          setStatus('error');
          setMessage(result.message || 'Payment verification failed');
        }
      } catch (error) {
        console.error('Verification error:', error);
        setStatus('error');
        setMessage('An error occurred during verification');
      }
    };

    verifyTransaction();
  }, [searchParams, verifyFunding]);

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const renderContent = () => {
    switch (status) {
      case 'verifying':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-primary-500/30">
              <Clock className="h-10 w-10 text-white animate-pulse" />
            </div>
            
            <div className="mb-2">
              <div className="w-1 h-6 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mx-auto mb-3" />
              <h2 className="text-xl font-bold text-gray-800">Verifying Payment</h2>
            </div>
            
            <p className="text-sm text-gray-500 mb-6">
              Please wait while we verify your payment...
            </p>

            <div className="max-w-xs mx-auto">
              <div className="h-1.5 bg-[#E8ECF5] rounded-full overflow-hidden shadow-neomorph-inset">
                <div className="h-full w-2/3 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full animate-pulse" />
              </div>
              <p className="text-xs text-gray-400 mt-2">This may take a few moments</p>
            </div>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-green-500/30">
              <CheckCircle className="h-10 w-10 text-white" />
            </div>
            
            <div className="mb-2">
              <div className="w-1 h-6 bg-gradient-to-b from-green-400 to-green-600 rounded-full mx-auto mb-3" />
              <h2 className="text-xl font-bold text-gray-800">Payment Successful!</h2>
            </div>
            
            <p className="text-sm text-gray-500 mb-6">{message}</p>
            
            {transaction && (
              <div className="bg-[#E8ECF5] rounded-xl p-5 shadow-neomorph-inset mb-6 max-w-sm mx-auto">
                <h3 className="text-sm font-medium text-gray-500 mb-3 text-left">Transaction Details</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Amount:</span>
                    <span className="text-base font-semibold text-gray-800">
                      {formatAmount(transaction.amount)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Reference:</span>
                    <span className="text-xs font-mono text-gray-600 bg-white px-2 py-1 rounded-md shadow-neomorph-inset">
                      {transaction.reference?.slice(0, 12)}...
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Status:</span>
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-50 text-green-700">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      {transaction.status}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg px-5 py-2.5 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-sm font-medium flex items-center justify-center"
              >
                <Wallet className="h-4 w-4 mr-2" />
                Go to Dashboard
              </button>
              <button
                onClick={() => navigate('/wallet')}
                className="bg-white rounded-lg px-5 py-2.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-sm font-medium text-gray-700 flex items-center justify-center border border-gray-100"
              >
                View Wallet
              </button>
            </div>
          </div>
        );

      case 'error':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-red-500/30">
              <XCircle className="h-10 w-10 text-white" />
            </div>
            
            <div className="mb-2">
              <div className="w-1 h-6 bg-gradient-to-b from-red-400 to-red-600 rounded-full mx-auto mb-3" />
              <h2 className="text-xl font-bold text-gray-800">Payment Failed</h2>
            </div>
            
            <p className="text-sm text-gray-500 mb-6">{message}</p>
            
            <div className="bg-yellow-50 rounded-xl p-5 shadow-neomorph-inset mb-6 max-w-sm mx-auto border border-yellow-200/50">
              <div className="flex items-start">
                <div className="p-1.5 bg-yellow-100 rounded-md shadow-neomorph-inset mr-3">
                  <AlertCircle className="h-4 w-4 text-yellow-600" />
                </div>
                <div className="text-sm text-yellow-700 text-left">
                  <p className="font-medium mb-1">Important:</p>
                  <p>If you were debited but received this error, please contact support with your transaction reference.</p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate('/wallet')}
                className="bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg px-5 py-2.5 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-sm font-medium flex items-center justify-center"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Try Again
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-white rounded-lg px-5 py-2.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-sm font-medium text-gray-700 flex items-center justify-center border border-gray-100"
              >
                Go to Dashboard
              </button>
            </div>

            <p className="text-xs text-gray-400 mt-6">
              Need help? Contact support at{' '}
              <a href="mailto:fasteltelecomltd@gmail.com" className="text-primary-600 hover:underline">
                fasteltelecomltd@gmail.com
              </a>
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#E8ECF5] flex items-center justify-center p-5">
      <div className="max-w-md w-full">
        {status !== 'verifying' && (
          <button
            onClick={() => navigate(-1)}
            className="mb-4 bg-white rounded-lg px-3 py-1.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-sm font-medium text-gray-600 flex items-center border border-gray-100"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            Back
          </button>
        )}

        <div className="bg-white rounded-xl p-6 shadow-neomorph border border-primary-100/30">
          {renderContent()}
        </div>

        <p className="text-center text-xs text-gray-400 mt-4">
          Secured by Fastel • {new Date().getFullYear()}
        </p>
      </div>

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default FundingCallbackPage;